import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { ServicesGrid } from '../components/ServicesGrid';
import { BrochureButton } from '../components/BrochureButton';
import { Gamepad2, Mail, Eye, MessageCircle, Check } from 'lucide-react';

const layers = [
  {
    icon: Gamepad2,
    title: 'Bewustwording creëren',
    text: 'Met het PuntUit-spel wordt het onderwerp omgangsvormen actief op de kaart gezet. Niet alleen als reactie op incidenten, maar juist ook in rustige tijden.',
  },
  {
    icon: Mail,
    title: 'Aandacht vasthouden',
    text: 'Met periodieke awareness-mails blijft het onderwerp terugkomen. Zo laat een organisatie zien dat een sociaal veilige omgeving blijvend prioriteit heeft.',
  },
  {
    icon: Eye,
    title: 'Vroeg signaleren via de Early Awareness Tool',
    text: 'Via praatuit.nl kan op geaggregeerd niveau worden gezien welke thema’s binnen een organisatie meer aandacht krijgen. Die signalen kunnen worden gebruikt voor de inhoud van awareness-mails en het jaarverslag, zodat organisaties gerichter kunnen bijsturen.',
  },
];

const spreekuurPoints = [
  'Eerste half uur per gesprek: inbegrepen in het abonnement',
  'In meer dan 70% van de gevallen is het spreekuur voldoende; er volgt dan geen aanvullende inzet',
  'Een afspraak is meestal de eerstvolgende werkdag mogelijk',
];

export function PreventieOngewenstGedrag() {
  return (
    <>
      <SEO title="Preventie & bewustwording" description="Voorkomen is beter dan genezen. PuntUit richt zich op bewustwording, het vasthouden van aandacht, vroeg signaleren en laagdrempelig contact via het gratis online spreekuur." canonicalUrl="/preventie-ongewenst-gedrag" />
      <SchemaMarkup
        type="service"
        pageUrl="https://puntuit.nl/preventie-ongewenst-gedrag"
        pageTitle="Preventie & bewustwording"
        pageDescription="Voorkomen is beter dan genezen. PuntUit richt zich op bewustwording, het vasthouden van aandacht, vroeg signaleren en laagdrempelig contact via het gratis online spreekuur."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Preventie & bewustwording', url: '/preventie-ongewenst-gedrag' },
        ]}
      />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Voorkomen is beter dan genezen.</h1>
            <p className="text-xl text-primary-100 mb-6">De aanpak van PuntUit richt zich op vier lagen: bewustwording, aandacht vasthouden, vroeg signaleren en laagdrempelig contact.</p>
            <div className="flex gap-4 flex-wrap items-center">
              <Link className="btn-primary" to="/contact">Plan een kennismakingsgesprek</Link>
              <BrochureButton variant="white" />
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container max-w-4xl">
            <p className="text-gray-700 text-lg leading-relaxed">Veel problemen beginnen klein. Als er geen veilige plek is om in een vroeg stadium te praten, groeien ze uit tot conflicten, uitval of escalaties.</p>
            <p className="text-gray-700 text-lg leading-relaxed mt-4">Twijfel je nog of je die veilige plek intern of extern belegt? Lees waarom een <Link to="/interne-vs-externe-vp" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">externe vertrouwenspersoon vrijwel altijd én goedkoper én beter</Link> is.</p>
          </div>
        </section>

        {/* Vier lagen */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-5xl">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">De aanpak</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">Vier lagen die elkaar versterken.</h2>
            <div className="space-y-6">
              {layers.map(({ icon: Icon, title, text }, i) => (
                <div key={title} className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm flex gap-5">
                  <div className="flex-shrink-0 flex flex-col items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0284c7] text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
                    <Icon className="w-5 h-5 text-[#0284c7]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-700 leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}

              {/* Laag 4 — gratis online spreekuur */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm flex gap-5">
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0284c7] text-white flex items-center justify-center text-sm font-bold">4</div>
                  <MessageCircle className="w-5 h-5 text-[#0284c7]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Laagdrempelig contact via het gratis online spreekuur</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">Het spreekuur is bedoeld om het contact met de vertrouwenspersoon zo laagdrempelig mogelijk te maken. Mensen hoeven geen officiële klacht te hebben om contact op te nemen; twijfel of ongemak is al genoeg reden.</p>
                  <p className="text-gray-700 leading-relaxed mb-5">Weet iemand niet zeker of een gesprek bij de vertrouwenspersoon thuishoort of dat de aanleiding belangrijk genoeg is? Bij twijfel inhalen. Kleine problemen laten zich makkelijker oplossen dan grote.</p>
                  <ul className="space-y-2">
                    {spreekuurPoints.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-gray-700">
                        <Check className="w-5 h-5 text-[#0284c7] mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServicesGrid />

        <section className="section-padding bg-[#0284c7] text-white">
          <div className="container text-center max-w-3xl">
            <h2 className="text-3xl font-bold mb-4">Voorkom escalatie. Begin vandaag.</h2>
            <p className="text-white/80 text-lg mb-8">Meer dan 500 organisaties vertrouwen op PuntUit voor preventie en vroegsignalering.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="bg-white text-[#0284c7] px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 hover:bg-gray-50 transition-colors">Plan een kennismakingsgesprek</Link>
              <BrochureButton variant="outline-white" />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
